import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Unified product data returned from the API.
 * Replaces the old PublicPackage and AddOn interfaces.
 */
export interface PublicProduct {
  id: string;
  productKey: string;
  type: 'bundle' | 'item';
  category: 'package' | 'invitations' | 'storage' | 'retention' | 'feature';
  displayName: string;
  description?: string;
  price: number; // RON (already converted from cents by backend)
  currency: string; // 'RON'
  attributes: {
    maxInvitations?: number;
    mediaStorageBytes?: number;
    maxFileSizeBytes?: number;
    maxVideoSizeBytes?: number;
    maxVideoDurationSec?: number;
    uploadsPerDevicePerDay?: number;
    mediaRetentionDays?: number;
    maxCollaborators?: number;
    analytics?: boolean;
    videoUploads?: boolean;
    moderationQueue?: boolean;
    uploadPortal?: boolean;
    liveWall?: boolean;
    mediaAlbum?: boolean;
  };
  versionId: string;
  includedProducts?: Array<{ productKey: string; quantity: number }>;
}

/**
 * Hook to fetch available products from the unified API.
 * Replaces usePackages() and separate add-on queries.
 */
export function useProducts() {
  return useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<PublicProduct[]>('/payments/products'),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
}

/**
 * Get bundle products (type === 'bundle')
 */
export function getBundles(products: PublicProduct[]): PublicProduct[] {
  return products.filter((p) => p.type === 'bundle');
}

/**
 * Get item products filtered by category
 */
export function getItems(
  products: PublicProduct[],
  category: PublicProduct['category'],
): PublicProduct[] {
  return products.filter((p) => p.type === 'item' && p.category === category);
}

/**
 * Get invitation pack products
 */
export function getInvitationPacks(products: PublicProduct[]): PublicProduct[] {
  return products
    .filter((p) => p.category === 'invitations')
    .sort((a, b) => (a.attributes.maxInvitations ?? 0) - (b.attributes.maxInvitations ?? 0));
}

/**
 * Compute optimal combination of invitation packs to cover a desired count.
 * Uses greedy algorithm: largest packs first, remainder with smallest.
 * Returns array of { productKey, quantity } sorted by pack size descending.
 */
export function computeInvitationPackCombo(
  products: PublicProduct[],
  neededCount: number,
): { productKey: string; quantity: number }[] {
  if (neededCount <= 0) return [];

  const packs = getInvitationPacks(products).filter(
    (p) => (p.attributes.maxInvitations ?? 0) > 0,
  );
  if (packs.length === 0) return [];

  // Sort descending by invitation count
  const sorted = [...packs].sort(
    (a, b) => (b.attributes.maxInvitations ?? 0) - (a.attributes.maxInvitations ?? 0),
  );

  const result: { productKey: string; quantity: number }[] = [];
  let remaining = neededCount;

  for (const pack of sorted) {
    const packSize = pack.attributes.maxInvitations ?? 0;
    if (packSize <= 0) continue;
    const count = Math.floor(remaining / packSize);
    if (count > 0) {
      result.push({ productKey: pack.productKey, quantity: count });
      remaining -= count * packSize;
    }
  }

  // If there's a remainder and the smallest pack exists, add enough to cover
  if (remaining > 0 && sorted.length > 0) {
    const smallest = sorted[sorted.length - 1];
    const smallestSize = smallest.attributes.maxInvitations ?? 1;
    const extraCount = Math.ceil(remaining / smallestSize);
    const existing = result.find((r) => r.productKey === smallest.productKey);
    if (existing) {
      existing.quantity += extraCount;
    } else {
      result.push({ productKey: smallest.productKey, quantity: extraCount });
    }
  }

  return result;
}

/**
 * Format a price in RON for display.
 * Price is already in RON (not cents).
 */
export function formatPrice(price: number, currency: string = 'RON'): string {
  if (price === 0) return 'Gratuit';
  return new Intl.NumberFormat('ro-RO', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Format limits for display (handles -1 = unlimited)
 */
export function formatLimit(value: number, unit: string = ''): string {
  if (value === -1) return 'Nelimitat';
  return `${value}${unit ? ` ${unit}` : ''}`;
}

/**
 * Get a color scheme based on product key
 */
export function getPackageColor(productKey: string): {
  bg: string;
  text: string;
  border: string;
} {
  switch (productKey.toLowerCase()) {
    case 'free':
      return {
        bg: 'bg-sage-100',
        text: 'text-sage-600',
        border: 'border-sage-200',
      };
    case 'general':
    case 'standard':
      return {
        bg: 'bg-primary/10',
        text: 'text-primary',
        border: 'border-primary/20',
      };
    case 'premium':
    case 'enterprise':
      return {
        bg: 'bg-terracotta-100',
        text: 'text-terracotta-600',
        border: 'border-terracotta-200',
      };
    default:
      return {
        bg: 'bg-secondary',
        text: 'text-secondary-foreground',
        border: 'border-border',
      };
  }
}
